import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { TierBadge } from "@/components/shared";
import { BUSINESS_TYPES, STAGES, TIERS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const p = user.profile || {};
  const [form, setForm] = useState({ business_type: p.business_type || "", stage: p.stage || "", goals: p.goals || "", bio: p.bio || "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try { await updateProfile(form); toast.success("Profile updated"); }
    catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <DashboardLayout>
      <div className="mb-8"><h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Profile & membership</h1></div>
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="profile-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label>Name</Label><Input value={user.name} disabled className="mt-1.5 h-11 bg-slate-50" /></div>
              <div><Label>Email</Label><Input value={user.email} disabled className="mt-1.5 h-11 bg-slate-50" /></div>
              <div>
                <Label>Business type</Label>
                <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="profile-business-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="profile-stage"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-5"><Label>Current goal</Label><Textarea value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} rows={2} className="mt-1.5" data-testid="profile-goals" /></div>
            <div className="mt-5"><Label>Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="mt-1.5" data-testid="profile-bio" placeholder="Tell the community about yourself" /></div>
            <Button onClick={save} disabled={busy} className="mt-6 rounded-md bg-navy text-white hover:bg-navysurface" data-testid="profile-save">{busy ? "Saving…" : "Save changes"}</Button>
          </div>
        </div>
        <div>
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <p className="text-sm text-slate-500">Current membership</p>
            <div className="mt-2"><TierBadge tier={user.tier} /></div>
            <p className="mt-4 text-sm text-slate-600">{user.tier === "pro" ? "You have full access to everything." : "Upgrade to unlock more resources, courses and premium community."}</p>
            {user.tier !== "pro" && <Button onClick={() => navigate("/pricing")} className="mt-4 w-full rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="profile-upgrade">Upgrade plan</Button>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
